import React, { useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/shared/utils/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";
import { Button } from "@/shared/ui/button";

interface Option {
  value: string;
  label: string;
}

interface AddNewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchValue: string;
  onConfirm: (value: any) => void;
}

interface CustomComboboxProps {
  options?: Option[];
  value: string;
  onChange: (value: string) => void;
  onAddNew?: (value: any) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  addNewLabel?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  // New props for flexible dialog
  AddNewDialog?: React.ComponentType<AddNewDialogProps>;
  showAddNew?: boolean;
  // Escape hatch: when provided, clicking "Add New" simply invokes this
  // callback (and closes the dropdown) instead of opening the built-in
  // AddNewDialog. Lets callers render their own dialog as a sibling.
  onAddNewClick?: () => void;
}

export default function CustomCombobox({ 
  options = [], 
  value, 
  onChange, 
  onAddNew,
  placeholder = "Select option...",
  searchPlaceholder = "Search options...",
  addNewLabel = "Add New",
  emptyMessage = "No options found.",
  className = "",
  disabled = false,
  AddNewDialog,
  showAddNew = true,
  onAddNewClick
}: CustomComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const filteredOptions = options
    .filter(option => option.label.toLowerCase().includes(searchValue.toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedOption = options.find(option => option.value === value);

  const handleSelect = (selectedValue: string) => {
    if (selectedValue === value) {
      onChange("");
    } else {
      onChange(selectedValue);
    }
    setOpen(false);
  };

  const handleAddNew = () => {
    setOpen(false);
    if (onAddNewClick) {
      onAddNewClick();
      return;
    }
    setDialogOpen(true);
  };

  const handleDialogConfirm = (newValue: any) => {
    if (onAddNew) {
      onAddNew(newValue);
    }
    setDialogOpen(false);
    setSearchValue("");
  };

  const handleDialogOpenChange = (newOpen: boolean) => {
    setDialogOpen(newOpen);
    if (!newOpen) {
      // Reset search value when dialog closes without confirmation
      setSearchValue("");
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchValue("");
    }
  };

  const shouldShowAddNew = showAddNew && (onAddNewClick || (onAddNew && AddNewDialog));

  return (
    <>
      <div className={cn("w-full", className)}>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className={cn(
                "w-full justify-between border-border bg-card text-foreground shadow-none hover:border-line-2 hover:bg-accent",
                !selectedOption && "text-muted-foreground"
              )}
              disabled={disabled}
            >
              <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.9} />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="flex max-h-[min(400px,70vh)] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden p-0"
            align="start"
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
            }}
          >
            <Command
              shouldFilter={false}
              className="flex flex-col overflow-hidden max-h-[min(400px,70vh)]"
            >
              <CommandInput
                placeholder={searchPlaceholder}
                value={searchValue}
                onValueChange={setSearchValue}
              />
              <CommandList
                className="flex-1 min-h-0 overflow-y-auto max-h-[300px] touch-pan-y overscroll-contain [WebkitOverflowScrolling:touch]"
                onTouchMove={(e) => {
                  e.stopPropagation();
                }}
              >
                {shouldShowAddNew && (
                  <CommandGroup>
                    <CommandItem
                      onSelect={handleAddNew}
                    className="cursor-pointer border border-primary/20 bg-primary/12 font-semibold text-primary transition-colors duration-150 hover:bg-primary/15 data-[selected=true]:bg-primary/15 data-[selected='true']:bg-primary/15"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {addNewLabel}
                      {searchValue && (
                        <span className="ml-2 opacity-70">
                          &quot;{searchValue}&quot;
                        </span>
                      )}
                    </CommandItem>
                  </CommandGroup>
                )}
                {filteredOptions.length === 0 ? (
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {filteredOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => handleSelect(option.value)}
                        className="cursor-pointer transition-colors duration-150"
                      >
                        <Check
                          className={`mr-2 h-4 w-4 ${
                            option.value === value ? "text-primary opacity-100" : "opacity-0"
                          }`}
                        />
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {AddNewDialog && (
        <AddNewDialog
          open={dialogOpen}
          onOpenChange={handleDialogOpenChange}
          searchValue={searchValue}
          onConfirm={handleDialogConfirm}
        />
      )}
    </>
  );
}
